/* Admin Settings — Application Parameters (admin-tunable runtime config,
   currently the document-storage subsystem) plus the Reset Database
   danger zone. Everything is admin-only on the server; the UI hides
   destructive controls when the role check fails so non-admins don't
   click into a 403. */
const { useState: useStateS, useEffect: useEffectS } = React;

function SettingsPage() {
  const [tab, setTab] = useStateS('general');
  const [showReset, setShowReset] = useStateS(false);
  const isAdmin = QH_DATA.activeRole?.id === 'admin';
  const canDelete = window.can('settings', 'delete');

  return (
    <>
      <div className="page-header">
        <div>
          <div className="breadcrumb"><span>Admin</span><I.ChevronRight s={10}/><span>Settings</span></div>
          <h1>Settings</h1>
          <div className="subtitle">System administration and dangerous operations.</div>
        </div>
      </div>
      <div className="tabs" style={{padding:'0 var(--pad)'}}>
        <button className={`tab ${tab === 'general' ? 'active' : ''}`} onClick={() => setTab('general')}>General</button>
        <button className={`tab ${tab === 'email'   ? 'active' : ''}`} onClick={() => setTab('email')}>Email</button>
      </div>
      <div className="page-body" style={{display:'flex',flexDirection:'column',gap:16}}>
        {tab === 'general' && (
          <>
            <ApplicationParametersCard isAdmin={isAdmin}/>

            <div className="card" style={{borderColor:'var(--neg)'}}>
              <div className="card-header" style={{color:'var(--neg)'}}>Danger zone</div>
              <div className="card-body">
                <div style={{display:'flex',alignItems:'flex-start',gap:16}}>
                  <div style={{flex:1}}>
                    <div style={{fontWeight:500,marginBottom:4}}>Reset database</div>
                    <div className="text-xs muted" style={{lineHeight:1.5}}>
                      Removes <b>all</b> members, schemes, events, transactions, finance applications, repayments,
                      guarantor cheques, defaulters, audit log entries, refresh tokens, document attachments, and
                      non-admin users. RBAC stays intact — pages, every role, and the full permissions matrix are
                      kept, along with the admin user, all notification templates, and your application parameters.
                      This action cannot be undone.
                    </div>
                  </div>
                  {(canDelete && isAdmin) ? (
                    <button className="btn danger" onClick={() => setShowReset(true)}><I.AlertCircle/> Reset database…</button>
                  ) : (
                    <span className="text-xs muted" title="Requires admin role + settings.delete permission">Admin role + settings.delete required</span>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
        {tab === 'email' && <EmailSettingsCard isAdmin={isAdmin}/>}
      </div>
      {showReset && <ResetDatabaseModal onClose={() => setShowReset(false)}/>}
    </>
  );
}

// Email tab — SMTP credentials + the "Send test email" button. Reads
// every email.* key from /admin/settings; the server returns the
// password as the literal string "********" so it never leaves the
// box, and we only PUT a new password value when the operator actually
// retypes it (an unchanged masked value is silently dropped by the
// server, see AdminEndpoints.IsSecretKey).
function EmailSettingsCard({ isAdmin }) {
  const PASSWORD_MASK = '********';
  const [loaded, setLoaded]     = useStateS(false);
  const [host, setHost]         = useStateS('');
  const [port, setPort]         = useStateS('465');
  const [user, setUser]         = useStateS('');
  const [password, setPassword] = useStateS('');
  const [ssl, setSsl]           = useStateS(true);
  const [fromAddress, setFromAddress] = useStateS('');
  const [fromName, setFromName] = useStateS('QH Management');
  const [savedAt, setSavedAt]   = useStateS(null);
  const [busy, setBusy]         = useStateS(false);
  const [err, setErr]           = useStateS('');
  const [info, setInfo]         = useStateS('');
  const [testTo, setTestTo]     = useStateS('');
  const [testResult, setTestResult] = useStateS(null);

  const load = async () => {
    setErr('');
    try {
      const rows = await window.QH_API.get('/admin/settings');
      const m = Object.fromEntries((rows || []).map(r => [r.key, r.value]));
      setHost(m['email.smtp_host']     || '');
      setPort(m['email.smtp_port']     || '465');
      setUser(m['email.smtp_user']     || '');
      // Display the server's masked placeholder so the operator sees
      // "a password is set" without us ever shipping the real value.
      setPassword(m['email.smtp_password'] || '');
      const sslRaw = (m['email.smtp_ssl'] ?? 'true').toString().toLowerCase();
      setSsl(sslRaw !== 'false' && sslRaw !== '0');
      setFromAddress(m['email.from_address'] || '');
      setFromName(m['email.from_name']      || 'QH Management');
      const updates = (rows || []).filter(r => r.key.startsWith('email.')).map(r => r.updatedAt).filter(Boolean).sort();
      setSavedAt(updates.length ? updates[updates.length - 1] : null);
      setLoaded(true);
    } catch (e) {
      setErr(e.message || 'Failed to load email settings.');
      setLoaded(true);
    }
  };

  useEffectS(() => { load(); }, []);

  const save = async () => {
    setErr(''); setInfo(''); setBusy(true);
    try {
      await window.QH_API.put('/admin/settings', {
        'email.smtp_host':     host.trim(),
        'email.smtp_port':     String(Math.max(1, Number(port) || 465)),
        'email.smtp_user':     user.trim(),
        // PASSWORD_MASK is filtered out server-side — sending it back is
        // a no-op so unchanged passwords aren't accidentally wiped.
        'email.smtp_password': password,
        'email.smtp_ssl':      ssl ? 'true' : 'false',
        'email.from_address':  fromAddress.trim(),
        'email.from_name':     fromName.trim() || 'QH Management',
      });
      setInfo('Saved.');
      await load();
    } catch (e) { setErr(e.message || 'Save failed.'); }
    finally { setBusy(false); }
  };

  const sendTest = async () => {
    setErr(''); setInfo(''); setTestResult(null); setBusy(true);
    try {
      const r = await window.QH_API.post('/admin/settings/email/test', { to: testTo.trim() });
      setTestResult(r);
    } catch (e) {
      setTestResult({ ok: false, message: e.message || 'Test failed.' });
    } finally { setBusy(false); }
  };

  if (!loaded) return <div className="card"><div className="card-body text-xs muted">Loading email settings…</div></div>;

  const ready = host.trim() && fromAddress.trim();

  return (
    <div className="card">
      <div className="card-header">
        Email · SMTP
        {savedAt && <span className="actions"><span className="muted text-xs">last updated {window.fmtDateTime(savedAt)}</span></span>}
      </div>
      <div className="card-body" style={{display:'flex',flexDirection:'column',gap:12}}>
        {!ready && (
          <div style={{padding:'10px 14px',background:'var(--bg-sunken)',color:'var(--warn)',borderRadius:6,fontSize:13,border:'1px solid var(--warn)'}}>
            <I.AlertCircle s={12}/> SMTP isn't configured yet. Users will sign in <b>without OTP</b> until you fill this in and save.
          </div>
        )}

        <div style={{display:'grid',gridTemplateColumns:'2fr 1fr',gap:12}}>
          <div className="field"><label>SMTP host</label>
            <input className="input font-mono" value={host} onChange={e => setHost(e.target.value)}
                   placeholder="mail.kamilhosting.com" disabled={!isAdmin || busy}/>
            <div className="hint">DNS name or IP of the outbound SMTP server.</div>
          </div>
          <div className="field"><label>Port</label>
            <input className="input font-mono" value={port} onChange={e => setPort(e.target.value.replace(/\D+/g,''))}
                   disabled={!isAdmin || busy}/>
            <div className="hint">465 = implicit SSL · 587 = STARTTLS · 25 = plain.</div>
          </div>
        </div>

        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
          <div className="field"><label>Username</label>
            <input className="input font-mono" value={user} onChange={e => setUser(e.target.value)}
                   placeholder="noreply@example.com" disabled={!isAdmin || busy}/>
          </div>
          <div className="field"><label>Password</label>
            <input className="input font-mono" type="password" value={password}
                   onChange={e => setPassword(e.target.value)}
                   placeholder={password === PASSWORD_MASK ? 'set — leave to keep' : 'SMTP auth password'}
                   onFocus={() => { if (password === PASSWORD_MASK) setPassword(''); }}
                   disabled={!isAdmin || busy}/>
            <div className="hint">Never returned by the server — leave as <code>{PASSWORD_MASK}</code> to keep the saved value.</div>
          </div>
        </div>

        <div style={{display:'grid',gridTemplateColumns:'2fr 1fr',gap:12,alignItems:'end'}}>
          <div className="field"><label>From address</label>
            <input className="input font-mono" value={fromAddress} onChange={e => setFromAddress(e.target.value)}
                   placeholder="noreply@example.com" disabled={!isAdmin || busy}/>
            <div className="hint">Most providers require this to match the username above.</div>
          </div>
          <div className="field"><label>SSL / TLS</label>
            <select className="select" value={ssl ? '1' : '0'} onChange={e => setSsl(e.target.value === '1')} disabled={!isAdmin || busy}>
              <option value="1">Enabled (465 implicit · 587 STARTTLS)</option>
              <option value="0">Disabled (plain SMTP)</option>
            </select>
          </div>
        </div>

        <div className="field"><label>From name</label>
          <input className="input" value={fromName} onChange={e => setFromName(e.target.value)}
                 placeholder="QH Management" disabled={!isAdmin || busy}/>
        </div>

        {err && <div style={{color:'var(--neg)',fontSize:12}}>{err}</div>}
        {info && <div style={{color:'var(--pos)',fontSize:12}}>{info}</div>}

        <div style={{display:'flex',gap:8}}>
          <button className="btn primary" onClick={save} disabled={!isAdmin || busy}><I.Check/> {busy ? 'Saving…' : 'Save settings'}</button>
        </div>

        <div style={{padding:14,background:'var(--bg-sunken)',borderRadius:6,marginTop:8}}>
          <div style={{fontWeight:500,marginBottom:8}}>Test email</div>
          <div className="text-xs muted" style={{marginBottom:8,lineHeight:1.5}}>
            Sends a branded test message to the address below using the <b>currently-saved</b> SMTP settings (so save your changes first). Test failures show the SMTP error verbatim — useful for diagnosing TLS / auth issues.
          </div>
          <div style={{display:'flex',gap:8}}>
            <input className="input font-mono" style={{flex:1}} value={testTo} onChange={e => setTestTo(e.target.value)}
                   placeholder="recipient@example.com" disabled={!isAdmin || busy}/>
            <button className="btn" onClick={sendTest} disabled={!isAdmin || busy || !testTo.trim() || !ready}>
              <I.Mail s={12}/> {busy ? 'Sending…' : 'Send test email'}
            </button>
          </div>
          {testResult && (
            <div style={{marginTop:8,padding:'8px 12px',borderRadius:4,fontSize:12,
                         background: testResult.ok ? 'var(--pos-soft)' : 'var(--neg-soft)',
                         color:      testResult.ok ? 'var(--pos)'      : 'var(--neg)'}}>
              {testResult.ok ? <><I.Check s={11}/> {testResult.message}</> : <><I.X s={11}/> {testResult.message}</>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Application Parameters — currently scoped to the documents subsystem
// (storage root, max upload size, allowed extensions). Loads the full
// /admin/settings list (so future keys appear automatically) but only
// renders form fields for the documents.* keys we know about. Path test
// hits a probe endpoint so the operator gets immediate feedback before
// any upload is attempted.
function ApplicationParametersCard({ isAdmin }) {
  const [loaded, setLoaded] = useStateS(false);
  const [rootPath, setRootPath] = useStateS('');
  const [maxMb, setMaxMb] = useStateS('10');
  const [allowedExt, setAllowedExt] = useStateS('');
  const [savedAt, setSavedAt] = useStateS(null);
  const [busy, setBusy] = useStateS(false);
  const [err, setErr] = useStateS('');
  const [info, setInfo] = useStateS('');
  const [probe, setProbe] = useStateS(null); // { ok, error, ... }

  const load = async () => {
    setErr('');
    try {
      const rows = await window.QH_API.get('/admin/settings');
      const m = Object.fromEntries((rows || []).map(r => [r.key, r.value]));
      setRootPath(m['documents.root_path'] || '');
      setMaxMb(m['documents.max_mb'] || '10');
      setAllowedExt(m['documents.allowed_ext'] || '');
      const updates = (rows || []).map(r => r.updatedAt).filter(Boolean).sort();
      setSavedAt(updates.length ? updates[updates.length - 1] : null);
      setLoaded(true);
    } catch (e) {
      setErr(e.message || 'Failed to load settings.');
      setLoaded(true);
    }
  };

  useEffectS(() => { load(); }, []);

  const save = async () => {
    setErr(''); setInfo(''); setBusy(true);
    try {
      await window.QH_API.put('/admin/settings', {
        'documents.root_path': rootPath.trim(),
        'documents.max_mb': String(Math.max(1, Number(maxMb) || 10)),
        'documents.allowed_ext': allowedExt.split(',').map(s => s.trim().toLowerCase().replace(/^\./, '')).filter(Boolean).join(','),
      });
      setInfo('Saved.');
      await load();
      // Re-test the path so the green/red badge reflects the new value.
      await testPath();
    } catch (e) { setErr(e.message || 'Save failed.'); }
    finally { setBusy(false); }
  };

  const testPath = async () => {
    setErr('');
    try {
      const r = await window.QH_API.get('/admin/settings/documents/test');
      setProbe(r);
    } catch (e) { setErr(e.message || 'Path test failed.'); }
  };

  // Show the prominent "set me" banner when no root has been saved yet
  // — uploads anywhere in the app will fail with a 422 until this is set.
  const needsRoot = loaded && !rootPath.trim();

  return (
    <div className="card">
      <div className="card-header">
        Application Parameters
        {savedAt && <span className="actions"><span className="muted text-xs">last updated {window.fmtDateTime(savedAt)}</span></span>}
      </div>
      <div className="card-body" style={{display:'flex',flexDirection:'column',gap:12}}>
        {needsRoot && (
          <div style={{padding:'10px 14px',background:'var(--bg-sunken)',color:'var(--warn)',borderRadius:6,fontSize:13,border:'1px solid var(--warn)'}}>
            <I.AlertCircle s={12}/> Document storage path is not set. Uploads on bank transfers, transactions and finance applications will be refused until an admin sets a path here.
          </div>
        )}

        <div className="field">
          <label>Document storage path</label>
          <div style={{display:'flex',gap:8}}>
            <input className="input font-mono" value={rootPath} onChange={e => setRootPath(e.target.value)}
              placeholder="e.g. D:\QH\Documents or \\\\server\\share\\qh-docs" disabled={!isAdmin || busy} style={{flex:1}}/>
            <button className="btn" onClick={testPath} disabled={busy || !rootPath.trim()}>Test path</button>
          </div>
          <div className="hint">Server-side filesystem path. The folder must exist and be writable by the QH service account. Uploads are organised under this path by entity type / year / month.</div>
          {probe && (
            <div style={{marginTop:6,fontSize:12,padding:'6px 10px',borderRadius:4,
              background: probe.ok ? 'var(--pos-soft)' : 'var(--neg-soft)',
              color: probe.ok ? 'var(--pos)' : 'var(--neg)'}}>
              {probe.ok ? <><I.Check s={11}/> Path exists and is writable.</> : <><I.X s={11}/> {probe.error}</>}
            </div>
          )}
        </div>

        <div style={{display:'grid',gridTemplateColumns:'1fr 2fr',gap:12}}>
          <div className="field">
            <label>Max upload size (MB)</label>
            <input className="input font-mono" value={maxMb} onChange={e => setMaxMb(e.target.value.replace(/\D+/g,''))}
              disabled={!isAdmin || busy}/>
            <div className="hint">Per-file cap. Default 10 MB.</div>
          </div>
          <div className="field">
            <label>Allowed file types</label>
            <input className="input font-mono" value={allowedExt} onChange={e => setAllowedExt(e.target.value)}
              placeholder="pdf,doc,docx,xls,xlsx,jpg,jpeg,png,…" disabled={!isAdmin || busy}/>
            <div className="hint">Comma-separated. Defaults cover all common image, PDF, Word and Excel formats.</div>
          </div>
        </div>

        {err && <div style={{padding:'8px 12px',background:'var(--bg-sunken)',color:'var(--neg)',borderRadius:6,fontSize:12}}>{err}</div>}
        {info && <div style={{padding:'8px 12px',background:'var(--bg-sunken)',color:'var(--pos)',borderRadius:6,fontSize:12}}>{info}</div>}

        <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
          {isAdmin ? (
            <button className="btn primary" onClick={save} disabled={busy || !loaded}><I.Check/> {busy ? 'Saving…' : 'Save parameters'}</button>
          ) : (
            <span className="text-xs muted">Read-only — admin role required to change parameters.</span>
          )}
        </div>
      </div>
    </div>
  );
}

function ResetDatabaseModal({ onClose }) {
  const [confirm, setConfirm] = useStateS('');
  const [busy, setBusy] = useStateS(false);
  const [err, setErr] = useStateS('');
  const [result, setResult] = useStateS(null);

  const submit = async () => {
    if (confirm !== 'RESET') { setErr('Type RESET (in capitals) to confirm.'); return; }
    setBusy(true); setErr('');
    try {
      const r = await window.QH_API.post('/admin/reset-database', { confirm });
      setResult(r);
      // Reload local store so the UI immediately reflects the empty state.
      try { await window.QH_STORE.reload(); } catch {}
    } catch (e) { setErr(e.message || 'Reset failed.'); }
    finally { setBusy(false); }
  };

  return (
    <div className="modal-backdrop" onClick={busy ? undefined : onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h3 style={{color:'var(--neg)'}}><I.AlertCircle s={14}/> Reset database</h3>
            <div className="text-xs muted" style={{marginTop: 2}}>This permanently deletes all data.</div>
          </div>
          <button className="icon-btn close" onClick={onClose} disabled={busy} style={{marginLeft: 'auto'}}><I.X/></button>
        </div>
        {!result ? (
          <>
            <div className="modal-body" style={{display:'flex',flexDirection:'column',gap:12}}>
              <div style={{padding:12,background:'var(--neg-soft)',color:'var(--neg)',borderRadius:4,fontSize:12,lineHeight:1.6}}>
                <b>You are about to delete:</b>
                <ul style={{margin:'6px 0 0 18px',padding:0}}>
                  <li>{QH_DATA.members?.length ?? 0} members</li>
                  <li>{QH_DATA.schemes?.length ?? 0} schemes &middot; {QH_DATA.events?.length ?? 0} events &middot; {QH_DATA.transactions?.length ?? 0} transactions</li>
                  <li>{QH_DATA.financeApps?.length ?? 0} finance applications &middot; {QH_DATA.repayments?.length ?? 0} repayments &middot; {QH_DATA.defaulters?.length ?? 0} defaulters</li>
                  <li>All audit log entries, refresh tokens and document attachments (files removed from disk too)</li>
                  <li>All non-admin users</li>
                </ul>
                <div style={{marginTop:8}}><b>Kept:</b> admin user · RBAC pages · all roles · permissions matrix · notification templates · application parameters.</div>
              </div>
              <div className="field">
                <label>Type <span className="font-mono" style={{fontWeight:600}}>RESET</span> to confirm</label>
                <input className="input font-mono" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="RESET" autoFocus/>
              </div>
              {err && <div style={{color:'var(--neg)',fontSize:12}}>{err}</div>}
            </div>
            <div className="modal-footer">
              <button className="btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
              <div style={{flex:1}}/>
              <button className="btn danger" onClick={submit} disabled={busy || confirm !== 'RESET'}>
                <I.AlertCircle/> {busy ? 'Resetting…' : 'Permanently reset database'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="modal-body" style={{display:'flex',flexDirection:'column',gap:12}}>
              <div style={{padding:12,background:'var(--pos-soft)',color:'var(--pos)',borderRadius:4,fontSize:12,display:'flex',alignItems:'center',gap:8}}>
                <I.Check/> Database reset. Removed rows across {result.tables} tables.
              </div>
              <div className="table-wrap" style={{maxHeight:280,overflow:'auto'}}>
                <table className="data">
                  <thead><tr><th>Table</th><th className="right">Rows deleted</th></tr></thead>
                  <tbody>
                    {Object.entries(result.rowsDeleted).map(([k, v]) => (
                      <tr key={k}><td className="font-mono text-xs">{k}</td><td className="num right">{v}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="modal-footer">
              <div style={{flex:1}}/>
              <button className="btn primary" onClick={onClose}>Close</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

window.SettingsPage = SettingsPage;
